/*
  fc-subscription-options.js

  Drives the theme-owned "Kies je voorkeur" subscription picker
  (snippets/fc-subscription-options.liquid). Successor of
  kaching-subscriptions-ui.js: the selling-plan radios live in our own markup
  inside the product form, so selection submits natively — this script only
  owns the parts Liquid can't:

  - Selected-option badge next to the "Kies je voorkeur:" heading.
  - Per-row quantity steppers, each row keeping its own quantity; the SELECTED
    row's quantity is mirrored into the form's real name="quantity" input on
    every change (no submit-capture hack needed — nothing re-renders prices
    from that input anymore).
  - Per-row price text: unit × row quantity, plus the bundle second product and
    the bundle discount % when the pdp-pref "Bundel en bespaar" tab is active
    (same math the Kaching version used; checkout-enforced by the bundle
    discount function app via _fc_bundle properties).

  One instance per [data-fc-sub-options], scoped to its closest section.
*/
(function () {
  'use strict';

  // Formatter driven by the shop's money format (e.g. "€{{amount_with_comma_separator}}").
  function makeFormatter(moneyFormat) {
    var tmpl = moneyFormat || '€{{amount_with_comma_separator}}';
    var parts = tmpl.split(/\{\{[^}]*\}\}/);
    var prefix = parts[0] || '';
    var suffix = parts.length > 1 ? parts[1] : '';
    return function (cents) {
      if (cents == null || isNaN(cents)) return '';
      var s = (Math.round(cents) / 100).toFixed(2).split('.');
      var intPart = s[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return prefix + intPart + ',' + s[1] + suffix;
    };
  }

  function setup(root) {
    var scope = root.closest('.shopify-section') || document;
    var form = root.closest('form');
    var fmt = makeFormatter(root.getAttribute('data-money-format'));

    /* The single submitting quantity field. Regular PDPs: the hidden input of
       the (now hidden) pdp-product-regular row. Combi/other: the
       quantity_selector block's input — associated to the form via the form
       attribute, so it's found through form.elements, not querySelector.
       Disabled inputs are never candidates (pdp-product-preference disables
       its fallback input server-side). Forms without any quantity field
       (featured-product sections) get a hidden one so row quantities submit. */
    var realInput = scope.querySelector('[data-pdp-regular-quantity]:not([disabled])');
    if (!realInput && form && form.elements) {
      for (var fi = 0; fi < form.elements.length; fi++) {
        var fel = form.elements[fi];
        if (fel.name === 'quantity' && !fel.disabled) {
          realInput = fel;
          break;
        }
      }
    }
    if (!realInput && form) {
      realInput = document.createElement('input');
      realInput.type = 'hidden';
      realInput.name = 'quantity';
      realInput.value = '1';
      form.appendChild(realInput);
    }

    var minQty = 1;
    var maxQty = Infinity;
    if (realInput) {
      var pMin = parseInt(realInput.getAttribute('min'), 10);
      var pMax = parseInt(realInput.getAttribute('max'), 10);
      if (!isNaN(pMin) && pMin > 0) minQty = pMin;
      if (!isNaN(pMax) && pMax > 0) maxQty = pMax;
    }
    function clamp(n) {
      if (isNaN(n)) n = minQty;
      if (n < minQty) n = minQty;
      if (n > maxQty) n = maxQty;
      return n;
    }

    var badge = root.querySelector('[data-fc-sub-badge]');
    var rows = [];
    root.querySelectorAll('[data-fc-sub-option]').forEach(function (card) {
      var unit = parseInt(card.getAttribute('data-unit-price'), 10);
      var compare = parseInt(card.getAttribute('data-compare-price'), 10);
      rows.push({
        key: card.getAttribute('data-fc-sub-key'),
        title: card.getAttribute('data-fc-sub-title') || '',
        card: card,
        radio: card.querySelector('.fc-sub-options__input'),
        priceEl: card.querySelector('[data-fc-sub-price]'),
        compareEl: card.querySelector('[data-fc-sub-compare]'),
        qtyEl: card.querySelector('[data-fc-sub-qty]'),
        unit: isNaN(unit) ? null : unit,
        compare: isNaN(compare) ? null : compare,
        qty: minQty,
      });
    });
    if (!rows.length) return;

    var oneTimeRow = rows.filter(function (r) { return r.key === 'one-time'; })[0] || null;

    function selectedRow() {
      for (var i = 0; i < rows.length; i++) {
        if (rows[i].radio && rows[i].radio.checked) return rows[i];
      }
      return null;
    }

    /* ---------------- Bundle context ----------------
       Package (Alleen/Bundel) + kit (Trial/Combi) selection lives in the
       theme's pdp-pref, outside this picker. Under Bundel each option price
       adds the chosen 2nd product (one-time, qty 1), matching how
       product-form.js#addBundleToCart adds to the cart (main × qty + 2nd × 1). */
    function getBundleSecondPrice() {
      var pref = scope.querySelector('.pdp-pref:not(.pdp-pref--regular)');
      if (!pref) return 0;
      var pkg = pref.querySelector('[data-option].active');
      if (!pkg || pkg.getAttribute('data-option') !== 'bundle') return 0;
      var kit = pref.querySelector('[data-bundle-option].active');
      var type = kit && kit.getAttribute('data-bundle-option');
      if (type === 'combi-kit' && kit) {
        return parseInt(kit.getAttribute('data-combi-kit-price'), 10) || 0;
      }
      if (type === 'trial-kit') {
        // Trial Kit includes a 2nd product only once one has actually been
        // ADDED (mirrors pdp-pref.js + the cart's getSecondVariantIdForBundle).
        var added = scope.querySelector('[data-added-bundle-image]');
        if (!added || added.classList.contains('hidden')) return 0;
        var c = document.querySelector('[data-combineer-variant-price]:checked');
        if (c) return parseInt(c.getAttribute('data-combineer-variant-price'), 10) || 0;
      }
      return 0;
    }

    /* The active kit's REAL bundle discount % (checkout-enforced by the bundle
       discount function app). Trial Kit only discounts once a 2nd product is
       added — no pair in the cart means the function applies nothing. */
    function getBundleDiscountPercent() {
      var pref = scope.querySelector('.pdp-pref:not(.pdp-pref--regular)');
      if (!pref) return 0;
      var pkg = pref.querySelector('[data-option].active');
      if (pkg && pkg.getAttribute('data-option') !== 'bundle') return 0;
      var kit = pref.querySelector('[data-bundle-option].active');
      var type = kit && kit.getAttribute('data-bundle-option');
      if (type === 'combi-kit') {
        return parseFloat(pref.getAttribute('data-combi-discount-percent')) || 0;
      }
      if (type === 'trial-kit') {
        var added = scope.querySelector('[data-added-bundle-image]');
        if (!added || added.classList.contains('hidden')) return 0;
        return parseFloat(pref.getAttribute('data-trial-discount-percent')) || 0;
      }
      return 0;
    }

    /* ---------------- Per-option pricing ----------------
       Each row shows its OWN quantity × unit price (+ the bundle 2nd product
       when Bundel is active). The bundle discount % applies to BOTH lines at
       checkout (stacking on top of the subscription price is the agreed
       behaviour), so the shown total is (unit×qty + 2nd) × (1 − pct). */
    function updatePrices() {
      var second = getBundleSecondPrice();
      var factor = 1 - getBundleDiscountPercent() / 100;
      rows.forEach(function (row) {
        if (row.unit == null) return;
        var qty = row.qty || minQty;
        if (row.priceEl) {
          var t = fmt(Math.round((row.unit * qty + second) * factor));
          if (t && row.priceEl.textContent !== t) row.priceEl.textContent = t;
        }
        if (!row.compareEl) return;
        // Struck anchor: with a bundle discount the one-time row anchors at the
        // pair BEFORE the korting, the subscription rows at the bundle's
        // one-time price — each strikethrough shows exactly what that row's
        // choice saves. Without one, the original compare-at behaviour stands.
        var full = null;
        if (factor < 1) {
          var oneTimeUnit = oneTimeRow && oneTimeRow.unit;
          full =
            row.key === 'one-time' || oneTimeUnit == null
              ? row.unit * qty + second
              : (oneTimeUnit * qty + second) * factor;
        } else if (row.compare != null && row.compare > row.unit) {
          full = row.compare * qty + second;
        }
        if (full != null) {
          var tc = fmt(Math.round(full));
          if (tc && row.compareEl.textContent !== tc) row.compareEl.textContent = tc;
          row.compareEl.hidden = false;
        } else {
          row.compareEl.hidden = true;
        }
      });
    }

    /* ---------------- Selection → badge + real quantity ---------------- */
    function syncSelection() {
      var sel = selectedRow();
      if (badge) {
        var text = sel ? sel.title : '';
        if (badge.textContent !== text) badge.textContent = text;
      }
      // Never overwrite the sold-out sentinel (value 0 from pdp-product-regular /
      // pdp-product-preference) — the submit button is disabled anyway.
      if (realInput && realInput.getAttribute('data-sold-out') !== 'true' && sel) {
        realInput.value = String(clamp(sel.qty));
      }
    }

    /* ---------------- Steppers ---------------- */
    rows.forEach(function (row) {
      row.card.querySelectorAll('[data-fc-sub-step]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var delta = parseInt(btn.getAttribute('data-fc-sub-step'), 10) || 0;
          row.qty = clamp((row.qty || minQty) + delta);
          if (row.qtyEl) row.qtyEl.textContent = String(row.qty);
          updatePrices();
          syncSelection();
        });
      });
      if (row.radio) {
        row.radio.addEventListener('change', function () {
          syncSelection();
        });
      }
    });

    /* ---------------- Bundle-context triggers ----------------
       Package/kit switches and trial-product add/remove all live outside this
       picker; setTimeout lets pdp-pref.js update its cards first, then we read. */
    var scheduled = false;
    function refresh() {
      scheduled = false;
      updatePrices();
      syncSelection();
    }
    function schedule() {
      if (scheduled) return;
      scheduled = true;
      setTimeout(refresh, 0);
    }
    var pref = scope.querySelector('.pdp-pref:not(.pdp-pref--regular)');
    if (pref) pref.addEventListener('click', schedule);
    document.querySelectorAll('[data-combineer-variant-price]').forEach(function (inp) {
      inp.addEventListener('change', schedule);
    });
    document
      .querySelectorAll('[data-add-bundle-submit-button], [data-remove-bundle-button]')
      .forEach(function (btn) {
        btn.addEventListener('click', schedule);
      });
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) schedule();
    });

    /* ---------------- Init ---------------- */
    // Liquid preselects the first recurring plan; if a product ends up with
    // no recurring plans at all, fall back to the one-time row.
    if (!selectedRow() && oneTimeRow && oneTimeRow.radio) {
      oneTimeRow.radio.checked = true;
    }
    refresh();
  }

  function boot() {
    document.querySelectorAll('[data-fc-sub-options]').forEach(function (root) {
      if (root.hasAttribute('data-fc-sub-ready')) return;
      root.setAttribute('data-fc-sub-ready', '');
      setup(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
